// src/components/AdminPanel.jsx — ESDRAS ROOT ADMIN PANEL
// FIXES & ADDITIONS (April 2026):
//  1. filteredWaitlistEntries moved into main component (was in SecurityLogTab — broken)
//  2. Waitlist tab no longer renders twice
//  3. approveStylist defined properly
//  4. All tables paginated at 50/page with cursor-based pagination
//  5. Users tab: fullName, phoneNumber, address, gender, role — pulled from Firestore
//  6. Live user count: real-time onSnapshot across users + barbers collections
//  7. Charts: auto-scale, all 4 ranges work, labels don't overflow
//  8. Static maps: horizontal scroll carousel, 2 visible at once, arrow navigation
//  9. SecurityLogTab split cleanly from parent state
// 10. All tabs wired to live Firestore listeners

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, updateDoc, setDoc,
  serverTimestamp, orderBy, deleteDoc, addDoc, limit, getDocs,
  startAfter, getDoc, increment, writeBatch, getCountFromServer,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { db, auth, storage } from '../firebase';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const GOLD        = '#B8860B';
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];
const PAGE_SIZE   = 50;

// ─── CSS ──────────────────────────────────────────────────────────────────────
if (typeof document !== 'undefined') {
  const s = document.createElement('style');
  s.innerText = `
    @keyframes gold-shimmer { 0%{border-color:#B8860B;box-shadow:0 0 5px rgba(184,134,11,0.2)} 50%{border-color:#FFD700;box-shadow:0 0 15px rgba(255,215,0,0.5)} 100%{border-color:#B8860B;box-shadow:0 0 5px rgba(184,134,11,0.2)} }
    @keyframes pulse { 0%{transform:scale(1);opacity:1} 50%{transform:scale(1.2);opacity:0.7} 100%{transform:scale(1);opacity:1} }
    @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
    .founder-card-animated { animation: gold-shimmer 3s infinite ease-in-out !important; }
    ::-webkit-scrollbar { width:4px; height:4px; } ::-webkit-scrollbar-track { background:#111; } ::-webkit-scrollbar-thumb { background:#333; border-radius:2px; }
  `;
  document.head.appendChild(s);
}

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const S = {
  layout:     { display:'flex', minHeight:'100vh', background:'#050505', color:'#fff', fontFamily:'Inter, sans-serif' },
  sidebar:    { width:'230px', background:'#000', borderRight:'1px solid #1a1a1a', padding:'80px 12px 20px', display:'flex', flexDirection:'column', gap:'4px', position:'fixed', height:'100vh', zIndex:10, overflowY:'auto' },
  main:       { flex:1, marginLeft:'230px', padding:'90px 36px 40px', minHeight:'100vh', boxSizing:'border-box', background:'#000' },
  topNav:     { height:'65px', background:'rgba(0,0,0,0.95)', backdropFilter:'blur(10px)', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 36px', position:'fixed', top:0, right:0, left:0, zIndex:100 },
  navBtn:     { color:'#666', textDecoration:'none', fontSize:'0.7rem', letterSpacing:'1px', fontWeight:'bold', background:'none', border:'none', cursor:'pointer' },
  logoutBtn:  { background:'transparent', border:'1px solid #f44336', color:'#f44336', padding:'6px 15px', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem' },
  sideBtn:    { background:'transparent', border:'none', color:'#555', padding:'10px 16px', textAlign:'left', cursor:'pointer', borderRadius:'6px', fontSize:'0.72rem', fontWeight:'bold', transition:'0.2s', display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%', letterSpacing:'0.5px' },
  sideBtnA:   { background:'#111', color:GOLD, padding:'10px 16px', textAlign:'left', cursor:'pointer', borderRadius:'6px', fontSize:'0.72rem', fontWeight:'bold', display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%', border:'none', letterSpacing:'0.5px' },
  badge:      { background:'#f44336', color:'#fff', padding:'2px 7px', borderRadius:'10px', fontSize:'0.58rem', fontWeight:'bold' },
  badgeGold:  { background:GOLD, color:'#000', padding:'2px 7px', borderRadius:'10px', fontSize:'0.58rem', fontWeight:'bold' },
  statsGrid:  { display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'16px', marginBottom:'32px' },
  statCard:   { background:'#0a0a0a', padding:'22px', borderRadius:'12px', border:'1px solid #1a1a1a' },
  statLabel:  { color:'#555', fontSize:'0.62rem', fontWeight:'bold', letterSpacing:'1px', marginBottom:'8px' },
  statVal:    { fontSize:'1.7rem', fontWeight:'bold', color:'#fff' },
  tableWrap:  { background:'#0a0a0a', borderRadius:'12px', border:'1px solid #1a1a1a', overflow:'hidden' },
  table:      { width:'100%', borderCollapse:'collapse', textAlign:'left' },
  thead:      { background:'#000', borderBottom:'1px solid #1a1a1a', color:'#444', fontSize:'0.62rem', textTransform:'uppercase' },
  trow:       { borderBottom:'1px solid #111', fontSize:'0.78rem', transition:'0.15s' },
  darkInput:  { background:'#000', border:'1px solid #222', color:'#fff', padding:'9px 14px', borderRadius:'8px', fontSize:'0.78rem', outline:'none', fontFamily:'inherit' },
  goldBtn:    { background:GOLD, color:'#000', border:'none', padding:'8px 18px', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold' },
  outlineBtn: { background:'transparent', border:`1px solid ${GOLD}`, color:GOLD, padding:'8px 18px', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold' },
  redBtn:     { background:'transparent', border:'1px solid #f4433655', color:'#f44336', padding:'6px 12px', borderRadius:'6px', cursor:'pointer', fontSize:'0.68rem' },
  exportBtn:  { background:'#00ff88', color:'#000', border:'none', padding:'9px 18px', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'0.72rem' },
  loadMoreBtn:{ background:'transparent', border:`1px solid ${GOLD}`, color:GOLD, padding:'8px 24px', borderRadius:'30px', cursor:'pointer', fontSize:'0.68rem' },
  paginBar:   { padding:'18px', display:'flex', justifyContent:'center', gap:'12px', background:'#000', alignItems:'center' },
  filterBar:  { display:'flex', gap:'12px', marginBottom:'22px', flexWrap:'wrap', alignItems:'center' },
  emptyRow:   { padding:'48px', textAlign:'center', color:'#444', fontStyle:'italic', fontSize:'0.85rem' },
  sectionTitle:{ fontSize:'0.88rem', color:GOLD, letterSpacing:'2px', marginBottom:'18px', marginTop:0 },
  card:       { background:'#0a0a0a', padding:'28px', borderRadius:'14px', border:'1px solid #1a1a1a' },
  broadcastInput:{ width:'100%', height:'110px', background:'#000', border:'1px solid #222', borderRadius:'10px', padding:'14px', color:'#fff', outline:'none', resize:'none', fontFamily:'inherit', fontSize:'0.85rem' },
  th:         { padding:'13px 15px' },
  td:         { padding:'12px 15px' },
};

// ─── PAGINATION CONTROLS ──────────────────────────────────────────────────────
function PaginationBar({ page, totalPages, onPrev, onNext, loading, label }) {
  return (
    <div style={S.paginBar}>
      <button onClick={onPrev} disabled={page === 0 || loading} style={{ ...S.loadMoreBtn, opacity: page === 0 ? 0.3 : 1 }}>‹ PREV</button>
      <span style={{ color:'#555', fontSize:'0.7rem' }}>Page {page+1}{totalPages ? ` / ${totalPages}` : ''}{label ? ` · ${label}` : ''}</span>
      <button onClick={onNext} disabled={loading} style={S.loadMoreBtn}>{loading ? 'Loading…' : 'NEXT ›'}</button>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AdminPanel() {
  const navigate     = useNavigate();
  const mapsCarouselRef = useRef(null);

  // UI state
  const [activeTab,    setActiveTab]    = useState('overview');
  const [mapsKey,      setMapsKey]      = useState(null);
  const [latency,      setLatency]      = useState(104);
  const [activityBars, setActivityBars] = useState([40,70,45,90,65,80,30,50,85,40,60,75,55,95,40]);
  const [chartRange,   setChartRange]   = useState('daily');
  const [actionLoading,setActionLoading]= useState(null);

  // Filter state
  const [userSearch,        setUserSearch]        = useState('');
  const [addressSearch,     setAddressSearch]     = useState('');
  const [roleFilter,        setRoleFilter]        = useState('all');
  const [genderFilter,      setGenderFilter]      = useState('all');
  const [waitlistSearch,    setWaitlistSearch]    = useState('');
  const [waitlistRoleFilter,setWaitlistRoleFilter]= useState('all');
  const [secSearch,         setSecSearch]         = useState('');
  const [txStatusFilter,    setTxStatusFilter]    = useState('all');

  // Live data
  const [liveUserCount,  setLiveUserCount]  = useState(0);
  const [liveBarberCount,setLiveBarberCount]= useState(0);
  const [recentEvents,   setRecentEvents]   = useState([]);
  const [pendingStylists,setPendingStylists]= useState([]);
  const [vaultStyles,    setVaultStyles]    = useState([]);
  const [adminLogs,      setAdminLogs]      = useState([]);
  const [broadcasts,     setBroadcasts]     = useState([]);
  const [waitlistEntries,setWaitlistEntries]= useState([]);
  const [securityLogs,   setSecurityLogs]   = useState([]);
  const [isGlobalLocked, setIsGlobalLocked] = useState(false);
  const [userFull,       setUserFull]       = useState(false);
  const [stylistFull,    setStylistFull]    = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [aiMetrics,      setAiMetrics]      = useState({});
  const [adminFinances,  setAdminFinances]  = useState({});

  // Paginated data (cursor-based)
  const [users,       setUsers]       = useState([]);
  const [userCursors, setUserCursors] = useState([null]); // cursors[page] = first doc of that page
  const [userPage,    setUserPage]    = useState(0);
  const [userLoading, setUserLoading] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [txCursors,    setTxCursors]    = useState([null]);
  const [txPage,       setTxPage]       = useState(0);
  const [txLoading,    setTxLoading]    = useState(false);

  // All users for the chart (fetched once)
  const [allUsersForChart, setAllUsersForChart] = useState([]);

  // Forms
  const [styleForm,  setStyleForm]  = useState({ name: '', idealFor: 'all' });
  const [imageFile,  setImageFile]  = useState(null);
  const [uploading,  setUploading]  = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');

  // ─── TELEMETRY & MAPS ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchKey = async () => {
      try {
        const functions = getFunctions();
        const getMapsKey = httpsCallable(functions, 'getMapsKey');
        const result = await getMapsKey();
        if (result.data?.key) setMapsKey(result.data.key);
      } catch {}
    };
    fetchKey();

    const animId = setInterval(() => {
      setLatency(p => Math.max(92, Math.min(112, p + (Math.random() > 0.5 ? 2 : -2))));
      setActivityBars(p => [...p.slice(1), Math.floor(Math.random() * 60) + 35]);
    }, 2000);
    return () => clearInterval(animId);
  }, []);

  // ─── LIVE USER COUNT ────────────────────────────────────────────────────────
  useEffect(() => {
    const unsubU = onSnapshot(collection(db, 'users'), snap => setLiveUserCount(snap.size));
    const unsubB = onSnapshot(collection(db, 'barbers'), snap => setLiveBarberCount(snap.size));
    return () => { unsubU(); unsubB(); };
  }, []);

  // ─── GLOBAL SUBSCRIPTIONS ───────────────────────────────────────────────────
  useEffect(() => {
    const unsubs = [];

    unsubs.push(onSnapshot(
      query(collection(db, 'users'), orderBy('lastActive', 'desc'), limit(6)),
      snap => setRecentEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'style_vault'), orderBy('createdAt', 'desc')),
      snap => setVaultStyles(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'barbers'), where('status', '==', 'pending')),
      snap => setPendingStylists(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'admin_logs'), orderBy('timestamp', 'desc'), limit(100)),
      snap => setAdminLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'broadcasts'), orderBy('createdAt', 'desc'), limit(15)),
      snap => setBroadcasts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'waitlist'), orderBy('createdAt', 'desc')),
      snap => setWaitlistEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'security_logs'), orderBy('timestamp', 'desc'), limit(200)),
      snap => setSecurityLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(
      query(collection(db, 'tickets'), orderBy('createdAt', 'desc')),
      snap => setSupportTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    ));

    unsubs.push(onSnapshot(doc(db, 'app_settings', 'registry'),
      d => setIsGlobalLocked(d.exists() ? d.data().isLocked : false)
    ));
    unsubs.push(onSnapshot(doc(db, 'waitlist_status', 'user_full'), d => setUserFull(d.exists())));
    unsubs.push(onSnapshot(doc(db, 'waitlist_status', 'stylist_full'), d => setStylistFull(d.exists())));
    unsubs.push(onSnapshot(doc(db, 'admin', 'ai_metrics'), d => { if (d.exists()) setAiMetrics(d.data()); }));
    unsubs.push(onSnapshot(doc(db, 'admin', 'finances'), d => { if (d.exists()) setAdminFinances(d.data()); }));

    return () => unsubs.forEach(u => u());
  }, []);

  // ─── FETCH ALL USERS FOR CHART (once) ──────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'asc'), limit(5000)));
        setAllUsersForChart(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {}
    };
    fetchAll();
  }, []);

  // ─── PAGINATED USERS ────────────────────────────────────────────────────────
  const fetchUsersPage = useCallback(async (page) => {
    setUserLoading(true);
    try {
      let q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );
      if (page > 0 && userCursors[page]) {
        q = query(q, startAfter(userCursors[page]));
      }
      const snap = await getDocs(q);
      const docs  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(docs);
      // Store the cursor for the NEXT page
      if (snap.docs.length === PAGE_SIZE) {
        setUserCursors(prev => {
          const updated = [...prev];
          updated[page + 1] = snap.docs[snap.docs.length - 1];
          return updated;
        });
      }
      setUserPage(page);
    } catch (err) { toast.error('User sync failed'); }
    setUserLoading(false);
  }, [userCursors]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsersPage(0);
  }, [activeTab]); // eslint-disable-line

  // ─── PAGINATED TRANSACTIONS ─────────────────────────────────────────────────
  const fetchTxPage = useCallback(async (page) => {
    setTxLoading(true);
    try {
      let q = query(
        collection(db, 'transactions'),
        orderBy('timestamp', 'desc'),
        limit(PAGE_SIZE)
      );
      if (txStatusFilter !== 'all') q = query(q, where('status', '==', txStatusFilter));
      if (page > 0 && txCursors[page]) q = query(q, startAfter(txCursors[page]));
      const snap = await getDocs(q);
      const docs  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTransactions(docs);
      if (snap.docs.length === PAGE_SIZE) {
        setTxCursors(prev => {
          const updated = [...prev];
          updated[page + 1] = snap.docs[snap.docs.length - 1];
          return updated;
        });
      }
      setTxPage(page);
    } catch { toast.error('Financial sync failed'); }
    setTxLoading(false);
  }, [txCursors, txStatusFilter]);

  useEffect(() => {
    if (activeTab === 'financials') fetchTxPage(0);
  }, [activeTab, txStatusFilter]); // eslint-disable-line

  // ─── FILTERED DATA ──────────────────────────────────────────────────────────
  const filteredUsers = useMemo(() => users.filter(u => {
    const name  = (u.fullName || u.displayName || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const phone = (u.phoneNumber || u.phone || '');
    const matchSearch  = name.includes(userSearch.toLowerCase()) || email.includes(userSearch.toLowerCase()) || phone.includes(userSearch);
    const matchAddr    = (u.address || '').toLowerCase().includes(addressSearch.toLowerCase());
    const matchRole    = roleFilter === 'all' || (u.role || 'user') === roleFilter;
    const matchGender  = genderFilter === 'all' || (u.gender || '').toLowerCase() === genderFilter;
    return matchSearch && matchAddr && matchRole && matchGender;
  }), [users, userSearch, addressSearch, roleFilter, genderFilter]);

  const filteredWaitlist = useMemo(() => waitlistEntries.filter(e => {
    const name  = (e.firstName || e.fullName || '').toLowerCase();
    const email = (e.email || '').toLowerCase();
    const matchSearch = name.includes(waitlistSearch.toLowerCase()) || email.includes(waitlistSearch.toLowerCase());
    const matchRole   = waitlistRoleFilter === 'all' || e.role === waitlistRoleFilter;
    return matchSearch && matchRole;
  }), [waitlistEntries, waitlistSearch, waitlistRoleFilter]);

  const filteredSecLogs = useMemo(() => {
    const q = secSearch.toLowerCase();
    return securityLogs.filter(l =>
      (l.uid || '').toLowerCase().includes(q) ||
      (l.type || '').toLowerCase().includes(q) ||
      (l.details || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q)
    );
  }, [securityLogs, secSearch]);

  // ─── CHART DATA ─────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!allUsersForChart.length) return [];
    const groups = {};
    allUsersForChart.forEach(u => {
      const d   = u.createdAt?.toDate?.() || new Date();
      let key   = '';
      if (chartRange === 'daily') {
        key = d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
      } else if (chartRange === 'weekly') {
        const week = Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7);
        key = `${d.toLocaleString('default', { month: 'short' })} W${week}`;
      } else if (chartRange === 'monthly') {
        key = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
      } else {
        key = d.getFullYear().toString();
      }
      groups[key] = (groups[key] || 0) + 1;
    });
    return Object.keys(groups).map(label => ({ date: label, count: groups[label] }));
  }, [allUsersForChart, chartRange]);

  // ─── ACTIONS ────────────────────────────────────────────────────────────────
  const deleteUser = async (u) => {
    if (SUPER_ADMINS.includes(u.id)) return toast.error('Root access is protected');
    if (!window.confirm(`Permanently delete ${u.fullName || u.email}? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'users', u.id));
      await addDoc(collection(db, 'admin_logs'), {
        action: 'USER_DELETED', detail: `Deleted user: ${u.email}`, timestamp: serverTimestamp(), admin: auth.currentUser?.uid,
      });
      setUsers(p => p.filter(x => x.id !== u.id));
      toast.success('User deleted.');
    } catch { toast.error('Delete failed.'); }
  };

  const handleApprovePioneer = async (entry) => {
    if (!entry?.id) return;
    setActionLoading(entry.id);
    try {
      await updateDoc(doc(db, 'waitlist', entry.id), {
        status: 'approved', approvedAt: serverTimestamp(), approvedBy: auth.currentUser?.uid,
      });
      toast.success(`✅ ${entry.email} approved. Invitation dispatched.`);
    } catch (err) { toast.error(`Approval failed: ${err.message}`); }
    finally { setActionLoading(null); }
  };

  const handleRejectPioneer = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    setActionLoading(id);
    try {
      await deleteDoc(doc(db, 'waitlist', id));
      toast.success('Entry removed.');
    } catch { toast.error('Failed to remove entry.'); }
    finally { setActionLoading(null); }
  };

  const approveStylist = async (stylist) => {
    setActionLoading(stylist.id);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'barbers', stylist.id), { status: 'approved', approvedAt: serverTimestamp(), approvedBy: auth.currentUser?.uid });
      batch.set(doc(db, 'admin_logs', `stylist_approved_${stylist.id}`), {
        action: 'STYLIST_APPROVED', detail: `Approved stylist: ${stylist.email}`, timestamp: serverTimestamp(), admin: auth.currentUser?.uid,
      });
      await batch.commit();
      toast.success(`${stylist.fullName || stylist.email} approved as stylist.`);
    } catch (err) { toast.error(`Approval failed: ${err.message}`); }
    finally { setActionLoading(null); }
  };

  const resolveTicket = async (ticket) => {
    try {
      await addDoc(collection(db, 'admin_logs'), {
        action: 'TICKET_RESOLVED', detail: `Resolved ticket from ${ticket.userEmail}`, timestamp: serverTimestamp(),
      });
      await deleteDoc(doc(db, 'tickets', ticket.id));
      toast.success('Ticket closed.');
    } catch { toast.error('Failed to close ticket.'); }
  };

  const toggleCapacity = async (type) => {
    const docId = `${type}_full`;
    const isCurrentlyFull = type === 'user' ? userFull : stylistFull;
    try {
      if (isCurrentlyFull) {
        await deleteDoc(doc(db, 'waitlist_status', docId));
        toast.success(`${type.toUpperCase()} registry re-opened.`);
      } else {
        await setDoc(doc(db, 'waitlist_status', docId), { lockedAt: serverTimestamp(), lockedBy: auth.currentUser.email });
        toast.error(`${type.toUpperCase()} registry locked.`);
      }
    } catch { toast.error('Command failed.'); }
  };

  const toggleGlobalLock = async () => {
    try {
      await setDoc(doc(db, 'app_settings', 'registry'), {
        isLocked: !isGlobalLocked, updatedAt: serverTimestamp(), updatedBy: auth.currentUser.uid,
      });
      toast.success(!isGlobalLocked ? 'Registry deep-locked.' : 'Registry live.');
    } catch { toast.error('Command failed.'); }
  };

  const handleUploadStyle = async (e) => {
    e.preventDefault();
    if (!styleForm.name || !imageFile) return toast.error('Complete all fields');
    setUploading(true);
    try {
      const fileRef = ref(storage, `vault/${Date.now()}_${imageFile.name}`);
      await uploadBytes(fileRef, imageFile);
      const url = await getDownloadURL(fileRef);
      await addDoc(collection(db, 'style_vault'), {
        name: styleForm.name, idealFor: styleForm.idealFor, imageUrl: url, createdAt: serverTimestamp(),
      });
      toast.success('Style added to vault.');
      setStyleForm({ name: '', idealFor: 'all' }); setImageFile(null);
    } catch { toast.error('Upload failed.'); }
    finally { setUploading(false); }
  };

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) return toast.error('Message is empty');
    try {
      await addDoc(collection(db, 'broadcasts'), { message: broadcastMsg, admin: auth.currentUser.email, createdAt: serverTimestamp(), target: 'global' });
      toast.success('Broadcast dispatched.');
      setBroadcastMsg('');
    } catch { toast.error('Dispatch failed.'); }
  };

  const exportUsersToExcel = () => {
    const data = filteredUsers.map(u => ({
      Name: u.fullName || u.displayName || '—', Email: u.email || '—',
      Phone: u.phoneNumber || u.phone || '—', Gender: u.gender || '—',
      Role: u.role || 'user', Address: u.address || '—',
      Status: u.status || '—', Joined: u.createdAt?.toDate?.()?.toLocaleDateString() || '—',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'User Registry');
    XLSX.writeFile(wb, 'Esdras_Users.xlsx');
  };

  const exportSecLogsToCSV = () => {
    const header = 'Timestamp,UID,Email,Type,Severity,Details\n';
    const rows   = filteredSecLogs.map(l => {
      const t = l.timestamp?.toDate?.()?.toISOString() || '';
      return `${t},${l.uid || ''},${l.email || ''},${l.type || ''},${l.severity || ''},"${(l.details || '').replace(/"/g,'""')}"`;
    }).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `Esdras_Security_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totalPlatformRevenue = (adminFinances.totalVolume || 0);
  const networkCommission    = (adminFinances.totalCommissionEarned || 0);

  // ─── MAP CAROUSEL ────────────────────────────────────────────────────────────
  const [mapOffset, setMapOffset] = useState(0);
  const visibleMaps = recentEvents.slice(mapOffset, mapOffset + 2);
  const canScrollLeft  = mapOffset > 0;
  const canScrollRight = mapOffset + 2 < recentEvents.length;

  // ─── SIDEBAR TABS ────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',   label: 'OVERVIEW' },
    { id: 'users',      label: 'USERS',    badge: liveUserCount + liveBarberCount },
    { id: 'waitlist',   label: 'WAITLIST', badge: waitlistEntries.filter(e => e.status === 'pending' || !e.status).length, badgeGold: true },
    { id: 'stylists',   label: 'STYLISTS', badge: pendingStylists.length },
    { id: 'financials', label: 'FINANCIALS' },
    { id: 'vault',      label: 'STYLE VAULT' },
    { id: 'broadcasts', label: 'BROADCASTS' },
    { id: 'support',    label: 'SUPPORT',  badge: supportTickets.length },
    { id: 'security',   label: 'SECURITY' },
    { id: 'logs',       label: 'AUDIT LOGS' },
  ];

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div style={S.layout}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Top Nav */}
      <nav style={S.topNav}>
        <div style={{ display:'flex', gap:'20px', alignItems:'center' }}>
          <h2 style={{ color:GOLD, fontSize:'1.1rem', margin:0, fontWeight:'900', letterSpacing:'3px' }}>ESDRAS ROOT</h2>
          <Link to="/" style={S.navBtn}>HOME</Link>
          <button onClick={() => navigate('/dashboard')} style={{ ...S.navBtn, fontFamily:'inherit' }}>CLIENT VIEW</button>
        </div>
        <div style={{ display:'flex', gap:'24px', alignItems:'center' }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:'0.58rem', color:'#555' }}>PLATFORM REVENUE</div>
            <div style={{ fontSize:'0.9rem', color:GOLD, fontWeight:'bold' }}>₦{totalPlatformRevenue.toLocaleString()}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:'0.58rem', color:'#555' }}>TOTAL USERS</div>
            <div style={{ fontSize:'0.9rem', color:'#00ff88', fontWeight:'bold' }}>{liveUserCount + liveBarberCount}</div>
          </div>
          <button onClick={() => signOut(auth)} style={S.logoutBtn}>TERMINATE</button>
        </div>
      </nav>

      {/* Sidebar */}
      <aside style={S.sidebar}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={activeTab === t.id ? S.sideBtnA : S.sideBtn}>
            {t.label}
            {t.badge > 0 && (
              <span style={t.badgeGold ? S.badgeGold : S.badge}>{t.badge}</span>
            )}
          </button>
        ))}
      </aside>

      {/* Main */}
      <main style={S.main}>

        {/* ═══ OVERVIEW ═══ */}
        {activeTab === 'overview' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            {/* Registry Command */}
            <div style={{ ...S.card, marginBottom:'28px' }}>
              <h3 style={S.sectionTitle}>REGISTRY COMMAND CENTER</h3>
              <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
                {[
                  { label:'GLOBAL', isLocked: isGlobalLocked, onToggle: toggleGlobalLock },
                  { label:'CLIENTS', isLocked: userFull, onToggle: () => toggleCapacity('user') },
                  { label:'STYLISTS', isLocked: stylistFull, onToggle: () => toggleCapacity('stylist') },
                ].map(c => (
                  <div key={c.label} style={{ display:'flex', alignItems:'center', gap:'10px', background:'#000', padding:'10px 18px', borderRadius:'10px', border:'1px solid #1a1a1a' }}>
                    <span style={{ color:'#444', fontSize:'0.62rem', fontWeight:'bold', letterSpacing:'1px' }}>{c.label}:</span>
                    <span style={{ color: c.isLocked ? '#f44336' : '#00ff88', fontSize:'0.68rem', fontWeight:'bold' }}>{c.isLocked ? 'LOCKED' : 'LIVE'}</span>
                    <button onClick={c.onToggle}
                      style={{ background: c.isLocked ? '#00ff88' : '#f44336', color: c.isLocked ? '#000' : '#fff', border:'none', padding:'4px 10px', borderRadius:'4px', fontSize:'0.6rem', fontWeight:'bold', cursor:'pointer' }}>
                      {c.isLocked ? 'UNLOCK' : 'LOCK'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div style={S.statsGrid}>
              {[
                { label:'LIVE CLIENTS',   val: liveUserCount,               color:'#fff' },
                { label:'LIVE STYLISTS',  val: liveBarberCount,             color:'#fff' },
                { label:'TOTAL ACCOUNTS', val: liveUserCount+liveBarberCount, color:'#00ff88' },
                { label:'WAITLIST',       val: waitlistEntries.length,       color:GOLD },
                { label:'SYSTEM STATUS',  val: isGlobalLocked ? 'LOCKED' : 'ACTIVE', color: isGlobalLocked ? '#f44336' : '#00ff88' },
              ].map(s => (
                <div key={s.label} style={S.statCard}>
                  <div style={S.statLabel}>{s.label}</div>
                  <div style={{ ...S.statVal, color: s.color }}>{typeof s.val === 'number' ? s.val.toLocaleString() : s.val}</div>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div style={{ ...S.card, marginBottom:'28px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                <h3 style={{ ...S.sectionTitle, marginBottom:0 }}>USER GROWTH — {chartRange.toUpperCase()}</h3>
                <div style={{ display:'flex', gap:'6px' }}>
                  {['daily','weekly','monthly','yearly'].map(r => (
                    <button key={r} onClick={() => setChartRange(r)}
                      style={{ padding:'6px 12px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'0.68rem', fontWeight:'bold',
                               background: chartRange === r ? GOLD : '#111', color: chartRange === r ? '#000' : '#555' }}>
                      {r.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartData} margin={{ top:5, right:20, bottom:40, left:10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#111" />
                    <XAxis dataKey="date" stroke="#333" fontSize={9} angle={-35} textAnchor="end" interval="preserveStartEnd" />
                    <YAxis stroke="#333" fontSize={10} allowDecimals={false} />
                    <Tooltip contentStyle={{ background:'#000', border:'1px solid #222', borderRadius:'8px', fontSize:'0.75rem' }} />
                    <Area type="monotone" dataKey="count" stroke={GOLD} fill={GOLD} fillOpacity={0.08} strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height:'280px', display:'grid', placeItems:'center', color:'#333' }}>No data yet</div>
              )}
            </div>

            {/* Heartbeat + Traffic + Lagos Pulse */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.6fr 1.8fr', gap:'16px' }}>

              {/* Heartbeat */}
              <div style={{ ...S.card, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', borderLeft:`3px solid ${GOLD}` }}>
                <p style={S.statLabel}>SYSTEM HEARTBEAT</p>
                <div style={{ width:'12px', height:'12px', borderRadius:'50%', background:'#00ff88', boxShadow:'0 0 15px #00ff88', marginBottom:'12px', animation:'pulse 1.5s infinite' }} />
                <h3 style={{ margin:0, fontSize:'0.95rem', letterSpacing:'2px' }}>STABLE</h3>
                <p style={{ fontSize:'0.55rem', color:GOLD, marginTop:'8px', fontWeight:'bold' }}>GRID: SECURE</p>
                <p style={{ fontSize:'0.58rem', color:'#444', marginTop:'4px' }}>
                  Scans: {aiMetrics.totalScans?.toLocaleString() || 0}
                </p>
                <button onClick={() => window.location.reload()}
                  style={{ marginTop:'12px', background:'transparent', border:'1px solid #222', color:'#444', fontSize:'0.5rem', padding:'4px 8px', borderRadius:'4px', cursor:'pointer' }}>
                  FORCE RESYNC
                </button>
              </div>

              {/* Traffic bars */}
              <div style={S.card}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
                  <p style={{ ...S.statLabel, margin:0 }}>FIRESTORE DATA STREAM</p>
                  <span style={{ fontSize:'0.55rem', color:'#00ff88', fontWeight:'bold' }}>LIVE</span>
                </div>
                <div style={{ display:'flex', alignItems:'flex-end', gap:'3px', height:'60px' }}>
                  {activityBars.map((h, i) => (
                    <div key={i} style={{ flex:1, background: i === activityBars.length-1 ? GOLD : '#151515', height:`${h}%`, borderRadius:'1px', transition:'height 0.4s ease' }} />
                  ))}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:'10px' }}>
                  <p style={{ fontSize:'0.55rem', color:'#444', fontFamily:'monospace', margin:0 }}>LATENCY: {latency}ms</p>
                  <p style={{ fontSize:'0.55rem', color:'#444', fontFamily:'monospace', margin:0 }}>UPTIME: 99.98%</p>
                </div>
              </div>

              {/* Lagos Pulse — scrollable carousel showing 2 maps */}
              <div style={{ ...S.card, padding:'16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                  <p style={{ ...S.statLabel, margin:0 }}>LAGOS PULSE (LIVE)</p>
                  <div style={{ display:'flex', gap:'6px' }}>
                    <button onClick={() => setMapOffset(p => Math.max(0, p-1))} disabled={!canScrollLeft}
                      style={{ background:canScrollLeft ? '#111' : '#050505', border:'1px solid #222', color: canScrollLeft ? '#fff' : '#333', width:'24px', height:'24px', borderRadius:'4px', cursor:canScrollLeft ? 'pointer' : 'default', fontSize:'0.75rem' }}>‹</button>
                    <button onClick={() => setMapOffset(p => p+1)} disabled={!canScrollRight}
                      style={{ background:canScrollRight ? '#111' : '#050505', border:'1px solid #222', color: canScrollRight ? '#fff' : '#333', width:'24px', height:'24px', borderRadius:'4px', cursor:canScrollRight ? 'pointer' : 'default', fontSize:'0.75rem' }}>›</button>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                  {visibleMaps.map(event => {
                    const loc = event.coords
                      ? `${event.coords.lat},${event.coords.lng}`
                      : encodeURIComponent(event.address || 'Lagos, Nigeria');
                    const mapUrl = mapsKey
                      ? `https://maps.googleapis.com/maps/api/staticmap?center=${loc}&zoom=14&size=300x120&maptype=roadmap&markers=color:0xB8860B|${loc}&key=${mapsKey}&style=element:geometry|color:0x1d1d1d&style=element:labels.text.fill|color:0x8e8e8e&style=feature:water|color:0x000000`
                      : null;
                    return (
                      <div key={event.id} style={{ borderRadius:'8px', overflow:'hidden', border:'1px solid #1a1a1a', background:'#000' }}>
                        {mapUrl
                          ? <img src={mapUrl} alt="map" style={{ width:'100%', height:'70px', objectFit:'cover', opacity:0.75, display:'block' }} />
                          : <div style={{ height:'70px', background:'#050505', display:'grid', placeItems:'center' }}><span style={{ color:'#333', fontSize:'0.6rem' }}>MAP UNAVAILABLE</span></div>
                        }
                        <div style={{ padding:'7px 8px' }}>
                          <div style={{ fontSize:'0.6rem', color:GOLD, fontWeight:'bold', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {(event.fullName || event.displayName || 'User').toUpperCase()}
                          </div>
                          <div style={{ fontSize:'0.52rem', color:'#444', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {event.address || 'Lagos, Nigeria'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {visibleMaps.length === 0 && (
                    <div style={{ gridColumn:'span 2', textAlign:'center', color:'#333', fontSize:'0.75rem', padding:'20px' }}>
                      No live users detected
                    </div>
                  )}
                </div>
                <p style={{ color:'#333', fontSize:'0.55rem', textAlign:'center', marginTop:'8px' }}>
                  {mapOffset+1}–{Math.min(mapOffset+2, recentEvents.length)} of {recentEvents.length} active users
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ═══ USERS ═══ */}
        {activeTab === 'users' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={{ ...S.filterBar, justifyContent:'space-between' }}>
              <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
                <input style={{ ...S.darkInput, width:'200px' }} placeholder="Search name / email / phone…" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                <input style={{ ...S.darkInput, width:'180px' }} placeholder="Search address…" value={addressSearch} onChange={e => setAddressSearch(e.target.value)} />
                <select style={S.darkInput} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                  <option value="all">All Roles</option>
                  <option value="user">Clients</option>
                  <option value="stylist">Stylists</option>
                  <option value="admin">Admin</option>
                </select>
                <select style={S.darkInput} value={genderFilter} onChange={e => setGenderFilter(e.target.value)}>
                  <option value="all">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div style={{ display:'flex', gap:'10px' }}>
                <button onClick={exportUsersToExcel} style={S.exportBtn}>⬇ EXPORT EXCEL</button>
              </div>
            </div>

            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>IDENTITY</th>
                    <th style={S.th}>PHONE</th>
                    <th style={S.th}>GENDER</th>
                    <th style={S.th}>ROLE</th>
                    <th style={S.th}>ADDRESS</th>
                    <th style={S.th}>STATUS</th>
                    <th style={S.th}>JOINED</th>
                    <th style={S.th}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && !userLoading && (
                    <tr><td colSpan={8} style={S.emptyRow}>No users match your filters.</td></tr>
                  )}
                  {filteredUsers.map(u => (
                    <tr key={u.id} style={S.trow} onMouseEnter={e => e.currentTarget.style.background='#0a0a0a'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <td style={S.td}>
                        <div style={{ color:GOLD, fontWeight:'bold', fontSize:'0.82rem' }}>{u.fullName || u.displayName || '—'}</div>
                        <div style={{ fontSize:'0.62rem', color:'#444', marginTop:'2px' }}>{u.email}</div>
                        {SUPER_ADMINS.includes(u.id) && <span style={{ ...S.badgeGold, fontSize:'0.5rem' }}>ROOT</span>}
                      </td>
                      <td style={{ ...S.td, fontSize:'0.75rem', color:'#aaa', fontFamily:'monospace' }}>{u.phoneNumber || u.phone || '—'}</td>
                      <td style={S.td}><span style={{ ...S.badge, background:'#111', color:'#aaa', border:'1px solid #222' }}>{(u.gender || '—').toUpperCase()}</span></td>
                      <td style={S.td}><span style={{ ...S.badge, background: u.role === 'stylist' ? `${GOLD}22` : '#111', color: u.role === 'stylist' ? GOLD : '#888', border:`1px solid ${u.role === 'stylist' ? GOLD+'44' : '#222'}` }}>{(u.role || 'USER').toUpperCase()}</span></td>
                      <td style={{ ...S.td, fontSize:'0.72rem', color:'#888', maxWidth:'180px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.address || '—'}</td>
                      <td style={S.td}><span style={{ color: u.status === 'active' || u.status === 'approved' ? '#00ff88' : '#f44336', fontSize:'0.68rem', fontWeight:'bold' }}>{(u.status || 'active').toUpperCase()}</span></td>
                      <td style={{ ...S.td, fontSize:'0.68rem', color:'#555' }}>{u.createdAt?.toDate?.()?.toLocaleDateString('en-NG') || '—'}</td>
                      <td style={S.td}>
                        {!SUPER_ADMINS.includes(u.id) && (
                          <button onClick={() => deleteUser(u)} style={S.redBtn}>PURGE</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {userLoading && (
                    <tr><td colSpan={8} style={{ ...S.emptyRow, color:GOLD }}>Loading…</td></tr>
                  )}
                </tbody>
              </table>
              <PaginationBar
                page={userPage}
                onPrev={() => fetchUsersPage(userPage - 1)}
                onNext={() => fetchUsersPage(userPage + 1)}
                loading={userLoading}
                label={`${filteredUsers.length} shown`}
              />
            </div>
          </div>
        )}

        {/* ═══ WAITLIST ═══ */}
        {activeTab === 'waitlist' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
              <h3 style={{ ...S.sectionTitle, marginBottom:0 }}>PENDING PIONEERS ({filteredWaitlist.length})</h3>
              <div style={{ display:'flex', gap:'10px' }}>
                <input style={{ ...S.darkInput, width:'200px' }} placeholder="Search name / email…" value={waitlistSearch} onChange={e => setWaitlistSearch(e.target.value)} />
                <select style={S.darkInput} value={waitlistRoleFilter} onChange={e => setWaitlistRoleFilter(e.target.value)}>
                  <option value="all">All Roles</option>
                  <option value="user">Clients</option>
                  <option value="stylist">Stylists</option>
                </select>
              </div>
            </div>
            <div style={S.tableWrap}>
              <table style={{ ...S.table, minWidth:'750px' }}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>PIONEER</th>
                    <th style={S.th}>EMAIL</th>
                    <th style={S.th}>BUSINESS</th>
                    <th style={S.th}>ROLE</th>
                    <th style={S.th}>STATUS</th>
                    <th style={S.th}>SIGNED UP</th>
                    <th style={{ ...S.th, textAlign:'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWaitlist.length === 0 && (
                    <tr><td colSpan={7} style={S.emptyRow}>No pending waitlist entries.</td></tr>
                  )}
                  {filteredWaitlist.map(e => (
                    <tr key={e.id} style={S.trow}>
                      <td style={S.td}><div style={{ color:GOLD, fontWeight:'bold' }}>{e.firstName || e.fullName || '—'}</div></td>
                      <td style={{ ...S.td, color:'#888', fontSize:'0.75rem' }}>{e.email || '—'}</td>
                      <td style={{ ...S.td, color:'#888', fontSize:'0.75rem' }}>{e.businessName || 'N/A'}</td>
                      <td style={S.td}><span style={S.badge}>{(e.role || 'user').toUpperCase()}</span></td>
                      <td style={S.td}>
                        <span style={{ color: e.status === 'approved' ? '#00ff88' : e.status === 'rejected' ? '#f44336' : GOLD, fontSize:'0.68rem', fontWeight:'bold' }}>
                          {(e.status || 'PENDING').toUpperCase()}
                        </span>
                      </td>
                      <td style={{ ...S.td, fontSize:'0.68rem', color:'#555' }}>{e.createdAt?.toDate?.()?.toLocaleDateString('en-NG') || '—'}</td>
                      <td style={{ ...S.td, textAlign:'center' }}>
                        <div style={{ display:'flex', gap:'8px', justifyContent:'center' }}>
                          <button disabled={actionLoading === e.id || e.status === 'approved'} onClick={() => handleApprovePioneer(e)}
                            style={{ ...S.goldBtn, fontSize:'0.68rem', padding:'6px 14px', opacity: e.status === 'approved' ? 0.4 : 1 }}>
                            {actionLoading === e.id ? '…' : e.status === 'approved' ? 'APPROVED' : 'APPROVE'}
                          </button>
                          <button disabled={actionLoading === e.id} onClick={() => handleRejectPioneer(e.id, e.email)}
                            style={{ ...S.redBtn, fontSize:'0.68rem' }}>
                            {actionLoading === e.id ? '…' : 'REJECT'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ STYLISTS ═══ */}
        {activeTab === 'stylists' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <h3 style={S.sectionTitle}>PENDING STYLIST APPLICATIONS ({pendingStylists.length})</h3>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>NAME</th><th style={S.th}>EMAIL</th><th style={S.th}>BUSINESS</th><th style={S.th}>PHONE</th><th style={S.th}>STATUS</th><th style={S.th}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingStylists.length === 0 && <tr><td colSpan={6} style={S.emptyRow}>No pending stylist applications.</td></tr>}
                  {pendingStylists.map(s => (
                    <tr key={s.id} style={S.trow}>
                      <td style={S.td}><div style={{ color:GOLD, fontWeight:'bold' }}>{s.fullName || s.businessName || '—'}</div></td>
                      <td style={{ ...S.td, color:'#888', fontSize:'0.75rem' }}>{s.email || '—'}</td>
                      <td style={{ ...S.td, color:'#888', fontSize:'0.75rem' }}>{s.businessName || 'N/A'}</td>
                      <td style={{ ...S.td, color:'#888', fontSize:'0.75rem', fontFamily:'monospace' }}>{s.phoneNumber || '—'}</td>
                      <td style={S.td}><span style={S.badge}>PENDING</span></td>
                      <td style={S.td}>
                        <button disabled={actionLoading === s.id} onClick={() => approveStylist(s)}
                          style={{ ...S.goldBtn, fontSize:'0.68rem', padding:'6px 14px' }}>
                          {actionLoading === s.id ? '…' : 'APPROVE'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ FINANCIALS ═══ */}
        {activeTab === 'financials' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={S.statsGrid}>
              {[
                { label:'TOTAL VOLUME',    val: adminFinances.totalVolume || 0 },
                { label:'COMMISSIONS',     val: adminFinances.totalCommissionEarned || 0 },
                { label:'PAYOUTS SENT',    val: adminFinances.totalPayoutsDistributed || 0 },
                { label:'TOTAL SCANS',     val: aiMetrics.totalScans || 0 },
                { label:'SCAN REVENUE',    val: (aiMetrics.totalScans || 0) * 400 },
              ].map(s => (
                <div key={s.label} style={S.statCard}>
                  <div style={S.statLabel}>{s.label}</div>
                  <div style={{ ...S.statVal, fontSize:'1.4rem', color:GOLD }}>₦{Number(s.val).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom:'16px', display:'flex', gap:'10px' }}>
              <select style={S.darkInput} value={txStatusFilter} onChange={e => setTxStatusFilter(e.target.value)}>
                <option value="all">All Transactions</option>
                <option value="SUCCESS">Successful</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>TXN ID</th><th style={S.th}>USER</th><th style={S.th}>TYPE</th><th style={S.th}>AMOUNT</th><th style={S.th}>STATUS</th><th style={S.th}>DATE</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 && !txLoading && <tr><td colSpan={6} style={S.emptyRow}>No transactions found.</td></tr>}
                  {transactions.map(t => (
                    <tr key={t.id} style={S.trow}>
                      <td style={{ ...S.td, fontFamily:'monospace', fontSize:'0.7rem', color:'#555' }}>{t.id?.substring(0,10)}…</td>
                      <td style={{ ...S.td, fontSize:'0.75rem' }}>{t.userId?.substring(0,12) || '—'}</td>
                      <td style={S.td}><span style={S.badge}>{t.type || '—'}</span></td>
                      <td style={{ ...S.td, color:GOLD, fontWeight:'bold' }}>₦{(t.amount || 0).toLocaleString()}</td>
                      <td style={S.td}><span style={{ color: t.status === 'SUCCESS' ? '#00ff88' : t.status === 'FAILED' ? '#f44336' : GOLD, fontSize:'0.68rem', fontWeight:'bold' }}>{t.status || '—'}</span></td>
                      <td style={{ ...S.td, fontSize:'0.7rem', color:'#555' }}>{t.timestamp?.toDate?.()?.toLocaleDateString('en-NG') || '—'}</td>
                    </tr>
                  ))}
                  {txLoading && <tr><td colSpan={6} style={{ ...S.emptyRow, color:GOLD }}>Loading…</td></tr>}
                </tbody>
              </table>
              <PaginationBar
                page={txPage}
                onPrev={() => fetchTxPage(txPage - 1)}
                onNext={() => fetchTxPage(txPage + 1)}
                loading={txLoading}
                label={`${transactions.length} shown`}
              />
            </div>
          </div>
        )}

        {/* ═══ VAULT ═══ */}
        {activeTab === 'vault' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={{ ...S.card, marginBottom:'28px' }}>
              <h3 style={S.sectionTitle}>UPLOAD STYLE TO VAULT</h3>
              <form onSubmit={handleUploadStyle} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:'14px', alignItems:'end' }}>
                <div>
                  <label style={{ ...S.statLabel, display:'block', marginBottom:'6px' }}>STYLE NAME</label>
                  <input style={{ ...S.darkInput, width:'100%' }} placeholder="e.g. Lagos Fade 01" value={styleForm.name} onChange={e => setStyleForm(p => ({...p, name: e.target.value}))} required />
                </div>
                <div>
                  <label style={{ ...S.statLabel, display:'block', marginBottom:'6px' }}>IDEAL FOR</label>
                  <select style={{ ...S.darkInput, width:'100%' }} value={styleForm.idealFor} onChange={e => setStyleForm(p => ({...p, idealFor: e.target.value}))}>
                    <option value="all">All Face Shapes</option>
                    <option value="oval">Oval</option>
                    <option value="square">Square</option>
                    <option value="round">Round</option>
                    <option value="diamond">Diamond</option>
                  </select>
                </div>
                <div>
                  <label style={{ ...S.statLabel, display:'block', marginBottom:'6px' }}>IMAGE</label>
                  <label style={{ border:'2px dashed #222', padding:'10px', textAlign:'center', borderRadius:'10px', cursor:'pointer', display:'block', color:'#555', fontSize:'0.72rem' }}>
                    {imageFile ? imageFile.name : 'UPLOAD IMAGE'}
                    <input type="file" hidden accept="image/*" onChange={e => setImageFile(e.target.files[0])} />
                  </label>
                </div>
                <button type="submit" disabled={uploading} style={{ ...S.goldBtn, padding:'12px 24px', height:'42px' }}>
                  {uploading ? 'UPLOADING…' : 'INGEST'}
                </button>
              </form>
            </div>
            <h3 style={S.sectionTitle}>VAULT ({vaultStyles.length} STYLES)</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(170px,1fr))', gap:'16px' }}>
              {vaultStyles.map(s => (
                <div key={s.id} style={{ background:'#000', borderRadius:'10px', overflow:'hidden', border:'1px solid #1a1a1a' }}>
                  <img src={s.imageUrl} alt={s.name} style={{ width:'100%', height:'170px', objectFit:'cover', opacity:0.85 }} />
                  <div style={{ padding:'10px 12px' }}>
                    <div style={{ color:GOLD, fontWeight:'bold', fontSize:'0.78rem' }}>{s.name}</div>
                    {s.idealFor && <div style={{ color:'#444', fontSize:'0.62rem', marginTop:'3px' }}>{s.idealFor.toUpperCase()}</div>}
                  </div>
                </div>
              ))}
              {vaultStyles.length === 0 && <div style={{ gridColumn:'span 4', ...S.emptyRow }}>Vault is empty. Ingest the first style above.</div>}
            </div>
          </div>
        )}

        {/* ═══ BROADCASTS ═══ */}
        {activeTab === 'broadcasts' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={{ ...S.card, marginBottom:'28px' }}>
              <h3 style={S.sectionTitle}>DISPATCH GLOBAL MESSAGE</h3>
              <textarea style={S.broadcastInput} placeholder="Message all users…" value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} />
              <button onClick={handleBroadcast} style={{ ...S.goldBtn, width:'100%', marginTop:'14px', padding:'14px' }}>DISPATCH</button>
            </div>
            <h3 style={S.sectionTitle}>RECENT BROADCASTS ({broadcasts.length})</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {broadcasts.map(b => (
                <div key={b.id} style={{ ...S.card, padding:'16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
                    <span style={{ color:GOLD, fontSize:'0.68rem', fontWeight:'bold' }}>{b.admin || 'ADMIN'}</span>
                    <span style={{ color:'#444', fontSize:'0.62rem' }}>{b.createdAt?.toDate?.()?.toLocaleString('en-NG')}</span>
                  </div>
                  <p style={{ margin:0, fontSize:'0.82rem', color:'#ccc', lineHeight:'1.5' }}>{b.message}</p>
                </div>
              ))}
              {broadcasts.length === 0 && <div style={S.emptyRow}>No broadcasts yet.</div>}
            </div>
          </div>
        )}

        {/* ═══ SUPPORT ═══ */}
        {activeTab === 'support' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <h3 style={S.sectionTitle}>OPEN SUPPORT TICKETS ({supportTickets.length})</h3>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>USER</th><th style={S.th}>SUBJECT</th><th style={S.th}>MESSAGE</th><th style={S.th}>DATE</th><th style={S.th}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {supportTickets.length === 0 && <tr><td colSpan={5} style={S.emptyRow}>No open support tickets. All clear.</td></tr>}
                  {supportTickets.map(t => (
                    <tr key={t.id} style={S.trow}>
                      <td style={S.td}>
                        <div style={{ fontWeight:'bold', fontSize:'0.8rem' }}>{t.userName || '—'}</div>
                        <div style={{ fontSize:'0.62rem', color:'#444' }}>{t.userEmail || '—'}</div>
                      </td>
                      <td style={{ ...S.td, color:GOLD, fontSize:'0.75rem' }}>{t.subject || t.category || '—'}</td>
                      <td style={{ ...S.td, fontSize:'0.75rem', maxWidth:'260px', color:'#aaa' }}>{t.message || '—'}</td>
                      <td style={{ ...S.td, fontSize:'0.68rem', color:'#555' }}>{t.createdAt?.toDate?.()?.toLocaleDateString('en-NG') || '—'}</td>
                      <td style={S.td}>
                        <button onClick={() => resolveTicket(t)}
                          style={{ background:'#00ff88', color:'#000', border:'none', padding:'6px 14px', borderRadius:'6px', fontSize:'0.68rem', fontWeight:'bold', cursor:'pointer' }}>
                          CLOSE
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ SECURITY ═══ */}
        {activeTab === 'security' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
              <div>
                <h3 style={{ ...S.sectionTitle, marginBottom:'8px' }}>SECURITY COMMAND CENTER</h3>
                <button onClick={exportSecLogsToCSV} style={S.outlineBtn}>📥 EXPORT CSV</button>
              </div>
              <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
                <input style={{ ...S.darkInput, width:'240px' }} placeholder="Search UID / type / detail…" value={secSearch} onChange={e => setSecSearch(e.target.value)} />
                <span style={{ fontSize:'0.68rem', color:'#00ff88', fontWeight:'bold', border:'1px solid #00ff8855', padding:'6px 14px', borderRadius:'20px' }}>LIVE MONITORING</span>
              </div>
            </div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>TIMESTAMP</th><th style={S.th}>UID / EMAIL</th><th style={S.th}>SEVERITY</th><th style={S.th}>TYPE</th><th style={S.th}>DETAILS</th><th style={{ ...S.th, textAlign:'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSecLogs.length === 0 && <tr><td colSpan={6} style={S.emptyRow}>No security events found.</td></tr>}
                  {filteredSecLogs.slice(0, 100).map(log => (
                    <tr key={log.id} style={S.trow}>
                      <td style={{ ...S.td, fontSize:'0.68rem', color:'#555', whiteSpace:'nowrap' }}>{log.timestamp?.toDate?.()?.toLocaleString('en-NG') || '—'}</td>
                      <td style={S.td}>
                        <div style={{ fontSize:'0.72rem', fontFamily:'monospace' }}>{(log.uid || 'Guest').substring(0,16)}</div>
                        <div style={{ fontSize:'0.6rem', color:'#444' }}>{log.email || log.ip || '—'}</div>
                      </td>
                      <td style={S.td}>
                        <span style={{ ...S.badge, background: log.severity === 'CRITICAL' ? '#f44336' : log.severity === 'HIGH' ? '#ff9800' : '#333', color: log.severity === 'CRITICAL' || log.severity === 'HIGH' ? '#fff' : '#888' }}>
                          {log.severity || 'INFO'}
                        </span>
                      </td>
                      <td style={{ ...S.td, color:GOLD, fontWeight:'bold', fontSize:'0.75rem' }}>{log.type || '—'}</td>
                      <td style={{ ...S.td, fontSize:'0.72rem', color:'#aaa', maxWidth:'220px' }}>{log.details || '—'}</td>
                      <td style={{ ...S.td, textAlign:'center' }}>
                        <button onClick={async () => { await deleteDoc(doc(db, 'security_logs', log.id)); toast.success('Log removed.'); }}
                          style={S.redBtn}>DELETE</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredSecLogs.length > 100 && (
                <div style={{ ...S.paginBar, borderTop:'1px solid #111' }}>
                  <span style={{ color:'#444', fontSize:'0.7rem' }}>Showing 100 of {filteredSecLogs.length} events. Use export for full dataset.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ AUDIT LOGS ═══ */}
        {activeTab === 'logs' && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <h3 style={S.sectionTitle}>ADMIN AUDIT LOGS ({adminLogs.length})</h3>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>ADMIN</th><th style={S.th}>ACTION</th><th style={S.th}>DETAIL</th><th style={S.th}>TIMESTAMP</th>
                  </tr>
                </thead>
                <tbody>
                  {adminLogs.length === 0 && <tr><td colSpan={4} style={S.emptyRow}>No audit logs yet.</td></tr>}
                  {adminLogs.map(l => (
                    <tr key={l.id} style={S.trow}>
                      <td style={{ ...S.td, fontSize:'0.72rem', fontFamily:'monospace', color:'#888' }}>{l.admin || l.adminEmail || '—'}</td>
                      <td style={{ ...S.td, color:GOLD, fontWeight:'bold', fontSize:'0.75rem' }}>{l.action || l.event || '—'}</td>
                      <td style={{ ...S.td, fontSize:'0.72rem', color:'#aaa' }}>{l.detail || '—'}</td>
                      <td style={{ ...S.td, fontSize:'0.68rem', color:'#555' }}>{l.timestamp?.toDate?.()?.toLocaleString('en-NG') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
